// Shared scheduling-window helpers for the live schedule pills + actionable
// sort used by SittingsList and the Dashboard sittings strip/grid.
//
// The server stamps each open/close window as a zone-less UTC timestamp (no
// trailing Z). A pill computed once at load freezes — a sitting that opens or
// closes while the page sits open never updates. These pure helpers (and the
// useScheduleTick hook below them) recompute the pill state + ordering against
// a coarse ticking clock, but ONLY while at least one row still has a future
// boundary, so an all-settled list never burns an idle timer.

import { useEffect, useState } from "react";

/** The schedule window of a row — both the SittingRow and the dashboard's
 *  AssessmentSummary expose `opens_at` / `closes_at` as nullable zone-less UTC
 *  strings, so this minimal shape covers both. */
export interface ScheduleWindow {
  opens_at?: string | null;
  closes_at?: string | null;
}

/** Parse a zone-less UTC stamp to epoch ms, or null when absent/unparseable.
 *  Appends "Z" so a bare server stamp parses as UTC (matching the pills), but
 *  tolerates an already-Z-suffixed value rather than double-suffixing it. */
export function parseScheduleStampMs(
  stamp: string | null | undefined,
): number | null {
  if (!stamp) return null;
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(stamp) ? stamp : stamp + "Z";
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** Parse both boundaries of a row's window in one go. */
export function scheduleBoundsMs(row: ScheduleWindow): {
  opens: number | null;
  closes: number | null;
} {
  return {
    opens: parseScheduleStampMs(row.opens_at),
    closes: parseScheduleStampMs(row.closes_at),
  };
}

/** Distance (ms) from `now` to the soonest FUTURE boundary across all rows, or
 *  null when nothing is still pending. Drives the gated interval's delay so the
 *  tick can tighten as a flip approaches. A boundary exactly at `now` has
 *  already flipped and is not counted. */
export function nearestFutureBoundaryMs(
  rows: readonly ScheduleWindow[],
  now: number,
): number | null {
  let nearest: number | null = null;
  for (const r of rows) {
    const { opens, closes } = scheduleBoundsMs(r);
    for (const b of [opens, closes]) {
      if (b == null) continue;
      const d = b - now;
      if (d > 0 && (nearest == null || d < nearest)) nearest = d;
    }
  }
  return nearest;
}

/** Whether ANY row still has a future opens_at or closes_at boundary — i.e.
 *  whether a pill / ordering could still change as the clock advances. The
 *  tick gate: false ⇒ everything is settled ⇒ stop ticking (no idle timer). */
export function hasPendingBoundary(
  rows: readonly ScheduleWindow[],
  now: number,
): boolean {
  return nearestFutureBoundaryMs(rows, now) != null;
}

// Actionable buckets, lowest sorts first. Open/live (can be entered now) leads,
// then not-yet-open (queued), then closed (done) sinks.
const BUCKET_OPEN = 0;
const BUCKET_NOT_YET_OPEN = 1;
const BUCKET_CLOSED = 2;

function bucket(opens: number | null, closes: number | null, now: number) {
  if (closes != null && now >= closes) return BUCKET_CLOSED;
  if (opens != null && now < opens) return BUCKET_NOT_YET_OPEN;
  return BUCKET_OPEN; // opened (or no opens_at) and not yet closed → live now
}

/** Actionable-first comparator for schedule windows, evaluated against `now`:
 *
 *    open/live  (soonest-closing first)
 *      → not-yet-open  (soonest-opening first)
 *        → closed
 *
 * An open row with no close (live indefinitely) sorts after rows that WILL
 * close, since those are the ones with a deadline to act on. Returns 0 for two
 * rows that tie in both bucket and timing so the surrounding sort stays stable. */
export function compareScheduleActionable(
  a: ScheduleWindow,
  b: ScheduleWindow,
  now: number,
): number {
  const ab = scheduleBoundsMs(a);
  const bb = scheduleBoundsMs(b);
  const aBucket = bucket(ab.opens, ab.closes, now);
  const bBucket = bucket(bb.opens, bb.closes, now);
  if (aBucket !== bBucket) return aBucket - bBucket;

  if (aBucket === BUCKET_OPEN) {
    // Soonest-closing first; a never-closing live row (null) sinks below any
    // row that has a closing deadline.
    const aClose = ab.closes ?? Infinity;
    const bClose = bb.closes ?? Infinity;
    return aClose - bClose;
  }
  if (aBucket === BUCKET_NOT_YET_OPEN) {
    // Soonest-opening first. opens is non-null in this bucket by construction.
    return (ab.opens ?? Infinity) - (bb.opens ?? Infinity);
  }
  // Both closed — order doesn't matter operationally; keep stable.
  return 0;
}

// Coarse re-render cadence for the schedule pills. Away from any boundary 30s
// keeps re-renders cheap; within ~30s of a flip it tightens to 5s so the pill
// and the open/closed state flip together right on the boundary instead of one
// lagging the other by up to half a minute. Mirrors the cadence the Dashboard
// used inline before this hook was extracted.
const COARSE_MS = 30_000;
const FINE_MS = 5_000;
const FINE_WINDOW_MS = 30_000;

/**
 * Returns a value that changes on a gated coarse interval (~5–30s) ONLY while
 * at least one row has a FUTURE opens_at or closes_at boundary. Wire it into a
 * pill/sort computation so they recompute as the clock crosses a boundary.
 *
 * The interval cleans itself up on unmount and stops entirely once nothing is
 * pending (no idle timer) — it re-arms automatically if `rows` later gains a
 * future boundary. The returned number is the current epoch-ms tick; callers
 * pass it straight into the schedule helpers as `now`.
 */
export function useScheduleTick(rows: readonly ScheduleWindow[]): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Re-evaluate against the live clock on (re)mount / rows change so a value
    // that went stale while the deps were unchanged catches up immediately.
    const start = Date.now();
    setNow(start);
    if (!hasPendingBoundary(rows, start)) return; // settled → no timer at all

    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const t = Date.now();
      setNow(t);
      const nearest = nearestFutureBoundaryMs(rows, t);
      if (nearest == null) return; // everything flipped → stop (no idle timer)
      const delay = nearest <= FINE_WINDOW_MS ? FINE_MS : COARSE_MS;
      timer = setTimeout(tick, delay);
    };
    const nearest = nearestFutureBoundaryMs(rows, start);
    const delay =
      nearest != null && nearest <= FINE_WINDOW_MS ? FINE_MS : COARSE_MS;
    timer = setTimeout(tick, delay);
    return () => clearTimeout(timer);
  }, [rows]);

  return now;
}
